import { useField } from 'formik';
import { useMemo, SyntheticEvent } from 'react';
import { Autocomplete, AutocompleteChangeReason } from '@mui/material';

import { controlError } from 'utils';

import { Icon } from '../icons';
import { Input, InputProps } from '../input';

export interface SelectOption {
  label: string;
  value: string | number;
}

export interface AutocompleteFieldProps extends Omit<
  InputProps,
  'name' | 'onChange'
> {
  name: string;
  label?: string;
  placeholder?: string;
  displayValue?: string;
  options: SelectOption[];
  mapValue?: (value: string) => unknown;
  onChange?: (event: { target: { name: string; value: string } }) => void;
}

export function AutocompleteField({
  name,
  label,
  options,
  required,
  onChange,
  mapValue,
  displayValue,
  placeholder = 'Select',
}: AutocompleteFieldProps) {
  const [field, meta, helpers] = useField<unknown>(name);
  const errorText = controlError(meta, name, label);

  const selectedValue = useMemo(
    () =>
      options.find(
        (option) => option.value === (displayValue ?? field.value)
      ) || null,
    [displayValue, field.value, options]
  );

  const handleChange = (
    _: SyntheticEvent,
    value: SelectOption | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason !== 'selectOption' && reason !== 'clear') return;

    const valueToChange = reason === 'clear' ? '' : String(value?.value ?? '');
    if (mapValue) {
      helpers.setValue(mapValue(valueToChange), true);
    } else {
      field.onChange({ target: { name, value: valueToChange } });
    }
    onChange?.({ target: { name, value: valueToChange } });
  };

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selectedValue}
      onChange={handleChange}
      popupIcon={<Icon.ChevronDown />}
      renderInput={(params) => (
        <Input
          {...params}
          name={name}
          label={label}
          required={required}
          error={!!errorText}
          onBlur={field.onBlur}
          helperText={errorText}
          placeholder={placeholder}
          slotProps={params.slotProps}
        />
      )}
    />
  );
}
